import type React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import CustomTextField from "../../../../components/common/Textfield";
import ResizableTextarea from "../../../../components/common/ResizableTextArea";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  AdditionalInformationResponse,
  CreateAdditionalInfoResponse,
  EventDetailsData,
  EventDetailsPageProps,
} from "./types/callsheet-event-details";

const EventDetailsPage: React.FC<EventDetailsPageProps> = ({
  callSheetData,
  reduxTheme,
  colors,
  eventDetails,
  setEventDetails,
}) => {
  const streamId = Number(useParams().id);

  const [notesId, setNotesId] = useState<number | null>(null);
  const [originalNotes, setOriginalNotes] = useState<
    EventDetailsData["notes"] | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);

  const notesSections = [
    { key: "travel", label: "Travel", icon: "🚗" },
    { key: "catering", label: "Catering", icon: "🍽️" },
    { key: "logistics", label: "Logistics", icon: "📋" },
    { key: "technical", label: "Technical", icon: "🔧" },
    { key: "special_requirements", label: "Special Requirements", icon: "⭐" },
    { key: "additional_notes", label: "Additional Notes", icon: "📝" },
  ];
  console.log("callsheet data", callSheetData);
  const fetchNotes = async () => {
    try {
      const response = await apiService.get<AdditionalInformationResponse>(
        `/api/streams/stream-additional-information/?stream=${streamId}`
      );

      const notesItem = response.data.results[0];

      if (notesItem) {
        setNotesId(notesItem.id);

        const updatedNotes = {
          travel: notesItem.travel_notes || "",
          catering: notesItem.catering_notes || "",
          logistics: notesItem.logistics_notes || "",
          technical: notesItem.technical_notes || "",
          special_requirements: notesItem.special_requirements_notes || "",
          additional_notes: notesItem.additional_notes || "",
        };

        setEventDetails((prev) => ({
          ...prev,
          notes: updatedNotes,
        }));

        setOriginalNotes(updatedNotes);
      }
    } catch (error) {
      console.error("Failed to fetch notes:", error);
      toast.error("Failed to load updated notes.");
    }
  };

  const handleSaveNotes = async () => {
    const payload = {
      stream: streamId,
      travel_notes: eventDetails.notes.travel,
      catering_notes: eventDetails.notes.catering,
      logistics_notes: eventDetails.notes.logistics,
      technical_notes: eventDetails.notes.technical,
      additional_notes: eventDetails.notes.additional_notes,
      special_requirements_notes: eventDetails.notes.special_requirements,
    };

    try {
      const response = await apiService.post<CreateAdditionalInfoResponse>(
        `/api/streams/stream-additional-information/`,
        payload
      );

      toast.success("Event details saved successfully ✅");
      setNotesId(response.data.id);
      fetchNotes();
    } catch (error) {
      console.error("Failed to save notes:", error);
      toast.error("Failed to save notes. Please try again.");
    }
  };

  const handleUpdateNotes = async () => {
    if (!notesId || !originalNotes) return;

    setIsSaving(true);

    // Build payload with only changed fields
    const changedPayload: Partial<Record<string, string>> = {};

    for (const key in eventDetails.notes) {
      const typedKey = key as keyof EventDetailsData["notes"];
      const newValue = eventDetails.notes[typedKey];
      const originalValue = originalNotes[typedKey];

      if (newValue !== originalValue) {
        changedPayload[`${typedKey}_notes`] = newValue;
      }
    }

    if (Object.keys(changedPayload).length === 0) {
      toast.info("No changes to update.");
      setIsSaving(false);
      return;
    }

    try {
      await apiService.patch(
        `/api/streams/stream-additional-information/${notesId}/`,
        changedPayload
      );

      toast.success("Notes updated successfully ✅");
      fetchNotes();
    } catch (error) {
      console.error("Failed to update notes:", error);
      toast.error("Failed to update notes.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof EventDetailsData, value: string) => {
    setEventDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNotesChange = (
    noteType: keyof EventDetailsData["notes"],
    value: string
  ) => {
    setEventDetails((prev) => ({
      ...prev,
      notes: {
        ...prev.notes,
        [noteType]: value,
      },
    }));
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        width: "100%",
        maxWidth: "210mm",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        lineHeight: 1.4,
        color: "#000",
        backgroundColor: "#fff",
        minHeight: "297mm",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: colors.primary[100],
            borderBottom: `2px solid ${reduxTheme.primary_colors[800]}`,
            pb: 2,
            mb: 3,
            textAlign: "center",
          }}
        >
          Event Details
        </Typography>
      </Box>
      {/* Main Content Grid */}
      {callSheetData?.booking?.booking_type !== "CONFERENCE" && (
        <Grid container spacing={4}>
          {/* Left Column - Program */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${reduxTheme.primary_colors[300]}`,
                height: "fit-content",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  color: colors.primary[100],
                  backgroundColor: reduxTheme.primary_colors[800],
                  p: 2,
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                Program
              </Typography>
              <CustomTextField
                multiline
                rows={12}
                fullWidth
                variant="outlined"
                placeholder="Enter the event program details here..."
                value={eventDetails.program}
                onChange={(e) => handleInputChange("program", e.target.value)}
              />
            </Paper>
          </Grid>
          {/* Right Column - Event Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${reduxTheme.primary_colors[300]}`,
                height: "fit-content",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  color: colors.primary[100],
                  backgroundColor: reduxTheme.primary_colors[800],
                  p: 2,
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                Event Information
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <CustomTextField
                  label="Priest Name"
                  variant="outlined"
                  fullWidth
                  disabled
                  value={callSheetData?.service?.preacher_name || ""}
                  onChange={(e) =>
                    handleInputChange("priest_name", e.target.value)
                  }
                />

                <CustomTextField
                  label="Eulogist Name"
                  variant="outlined"
                  fullWidth
                  disabled
                  value={callSheetData?.service?.eulogist_name || ""}
                  onChange={(e) =>
                    handleInputChange("eulogist_name", e.target.value)
                  }
                />

                <CustomTextField
                  label="MC Name"
                  variant="outlined"
                  fullWidth
                  disabled
                  value={callSheetData?.service?.mc_name || ""}
                  onChange={(e) => handleInputChange("mc_name", e.target.value)}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      {/* Notes Sections */}
      <Box sx={{ mt: 4 }}>
        {callSheetData?.booking?.booking_type !== "CONFERENCE" && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 3,
              color: colors.primary[100],
              textAlign: "center",
              borderBottom: `1px solid ${reduxTheme.primary_colors[300]}`,
              pb: 2,
            }}
          >
            Notes & Additional Information
          </Typography>
        )}
        <Grid container spacing={3}>
          {notesSections.map((section) => (
            <Grid size={{ xs: 12, md: 6 }} key={section.key}>
              <Accordion
                defaultExpanded
                sx={{
                  backgroundColor: "transparent",
                  boxShadow: 0,
                  border: `1px solid ${reduxTheme.primary_colors[300]}`,
                  borderRadius: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon sx={{ color: colors.primary[100] }} />
                  }
                  sx={{
                    backgroundColor: reduxTheme.primary_colors[800],
                    color: colors.primary[100],
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span style={{ fontSize: "18px" }}>{section.icon}</span>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "medium" }}
                    >
                      {section.label}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    borderTop: `1px solid ${reduxTheme.primary_colors[300]}`,
                  }}
                >
                  <ResizableTextarea
                    label={section.label}
                    value={
                      eventDetails.notes[
                        section.key as keyof EventDetailsData["notes"]
                      ]
                    }
                    onChange={(val) =>
                      handleNotesChange(
                        section.key as keyof EventDetailsData["notes"],
                        val
                      )
                    }
                    minRows={3}
                    maxRows={12}
                    colors={colors}
                    reduxTheme={reduxTheme}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>

        {/* Save or Update Button */}
        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={notesId ? handleUpdateNotes : handleSaveNotes}
            disabled={isSaving}
            sx={{ px: 4, py: 1.5, fontWeight: "bold", borderRadius: 2 }}
          >
            {isSaving ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : notesId ? (
              "Update Notes"
            ) : (
              "Save Notes"
            )}
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
};

export default EventDetailsPage;
